import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaExclamationTriangle } from 'react-icons/fa';
import { getStatusLabel } from '../constants/ticketStatuses';
import './TicketAgentNotices.css';

// KI-272 / KI-273 (Kevin 23/10): the ticket's status is the AI agent's own
// choice; when its actions wait for the team, it shows here as a proposal to
// apply or ignore. A carrier address that refused a message is signalled here
// so the team fixes the carrier page or the rule that built it.

const formatDate = (value) => {
    if (!value) return '';
    const raw = typeof value === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? `${value}Z` : value;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

const TicketAgentNotices = ({ ticket, onTicketChange }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!ticket) return null;
    const proposal = ticket.status_proposal && ticket.status_proposal.status ? ticket.status_proposal : null;
    const refusals = Array.isArray(ticket.carrier_address_refusals) ? ticket.carrier_address_refusals : [];
    if (!proposal && refusals.length === 0) return null;

    const heldText = {
        client_off: t('agentNotices.held.clientOff', "the B2B client's automation is off"),
        account_off: t('agentNotices.held.accountOff', "the account's automation is off"),
        team: t('agentNotices.held.team', 'a person handles this ticket'),
        pilot: t('agentNotices.held.pilot', 'the AI wake-up runs in pilot mode'),
        draft: t('agentNotices.held.draft', 'it goes with the AI draft waiting for validation'),
    };
    const originText = {
        carrier_page_address: t('agentNotices.origin.address', 'an address of the carrier page'),
        carrier_page_template: t('agentNotices.origin.template', 'the address template of the carrier page'),
        routing_rule: t('agentNotices.origin.rule', 'a routing rule of the carrier page'),
        contact_rules: t('agentNotices.origin.contactRules', 'the contact rules of the carrier page'),
        not_on_carrier_page: t('agentNotices.origin.unconfigured', 'an address not configured on the carrier page'),
    };

    const act = async (action) => {
        setBusy(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(`/api/tickets/${ticket._id}/status-proposal/${action}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (action === 'apply') {
                onTicketChange && onTicketChange({ status: response.data.status, status_proposal: null });
            } else {
                onTicketChange && onTicketChange({ status_proposal: null });
            }
        } catch (err) {
            setError(t('agentNotices.actionFailed', 'The action failed, please retry.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="agent-notices">
            {proposal && (
                <div className="agent-notice agent-notice--proposal" role="status">
                    <FaRobot className="agent-notice__icon" aria-hidden="true" />
                    <div className="agent-notice__body">
                        <div className="agent-notice__title">
                            {t('agentNotices.proposalTitle', 'Status proposed by the AI: {{status}}', {
                                status: getStatusLabel(t, proposal.status),
                            })}
                        </div>
                        <div className="agent-notice__text">
                            {t('agentNotices.proposalHeld', 'Not applied: {{reason}}.', {
                                reason: heldText[proposal.held] || heldText.draft,
                            })}
                            {proposal.summary ? ` ${proposal.summary}` : ''}
                        </div>
                        {error && <div className="agent-notice__error">{error}</div>}
                    </div>
                    <div className="agent-notice__actions">
                        <button type="button" className="agent-notice__btn agent-notice__btn--primary"
                                disabled={busy} onClick={() => act('apply')}>
                            {t('agentNotices.apply', 'Apply')}
                        </button>
                        <button type="button" className="agent-notice__btn" disabled={busy} onClick={() => act('dismiss')}>
                            {t('agentNotices.ignore', 'Ignore')}
                        </button>
                    </div>
                </div>
            )}
            {refusals.length > 0 && (
                <div className="agent-notice agent-notice--refused" role="alert">
                    <FaExclamationTriangle className="agent-notice__icon" aria-hidden="true" />
                    <div className="agent-notice__body">
                        <div className="agent-notice__title">
                            {t('agentNotices.refusedTitle', 'Carrier address refused — fix the carrier page or the rule')}
                        </div>
                        <ul className="agent-notice__list">
                            {refusals.map((entry) => (
                                <li key={`${entry.address}-${entry.conversation_id || ''}`}>
                                    <span className="agent-notice__address">{entry.address}</span>
                                    {' '}
                                    {t('agentNotices.refusedLine', '({{code}}, {{date}}) — from {{origin}}', {
                                        code: entry.code || t('agentNotices.noCode', 'no code'),
                                        date: formatDate(entry.at),
                                        origin: originText[entry.origin] || originText.not_on_carrier_page,
                                    })}
                                    {entry.origin_entry ? ` « ${entry.origin_entry} »` : ''}
                                    {entry.carrier_page_id && (
                                        <>
                                            {' · '}
                                            <Link to={`/carriers-instructions/${entry.carrier_page_id}`}>
                                                {t('agentNotices.openCarrierPage', 'Open the {{carrier}} carrier page', {
                                                    carrier: entry.carrier || '',
                                                })}
                                            </Link>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketAgentNotices;
